import React, { useState, useEffect } from "react";
import { CheckCircle, XCircle, Loader, Edit2 } from "lucide-react";
import PrimaryButton from "../ui/PrimaryButton";
import ExcelPreviewProcessor from "../utils/excelPreviewProcessorCrrem";

/**
 * Component for previewing and configuring Excel sheets before upload
 */
const ExcelPreview = ({ file, onCancel, onUpload }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [sheetConfigs, setSheetConfigs] = useState({});
  const [activeSheet, setActiveSheet] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!file) {
      setLoading(false);
      return;
    }

    const analyzeFile = async () => {
      try {
        setLoading(true);
        setError(null);

        const info = await ExcelPreviewProcessor.extractSheetInfo(file);
        setFileInfo(info);

        const configs = {};
        info.sheetNames.forEach((name) => {
          configs[name] = {
            originalName: name,
            newName: name,
            include: info.sheetsPreview[name].isValid,
            isValid: info.sheetsPreview[name].isValid,
          };
        });

        setSheetConfigs(configs);

        if (info.sheetNames.length > 0) {
          setActiveSheet(info.sheetNames[0]);
        }
      } catch (err) {
        console.error("Error analyzing Excel file:", err);
        setError("Failed to process Excel file. Please check the file format.");
      } finally {
        setLoading(false);
      }
    };

    analyzeFile();
  }, [file]);

  const handleNameChange = (sheetName, newName) => {
    setSheetConfigs((prev) => ({
      ...prev,
      [sheetName]: {
        ...prev[sheetName],
        newName,
      },
    }));
  };

  const handleIncludeToggle = (sheetName) => {
    setSheetConfigs((prev) => ({
      ...prev,
      [sheetName]: {
        ...prev[sheetName],
        include: !prev[sheetName].include,
      },
    }));
  };

  const handleUpload = async () => {
    try {
      setIsSubmitting(true);

      const selectedSheets = Object.entries(sheetConfigs)
        .filter(([_, config]) => config.include)
        .map(([name, config]) => ({
          originalName: name,
          newName: config.newName,
        }));

      if (selectedSheets.length === 0) {
        setError("Please select at least one sheet to upload.");
        return;
      }

      await onUpload(selectedSheets);
    } catch (err) {
      console.error("Error uploading sheets:", err);
      setError(err.message || "Failed to upload sheets");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center p-6">
          <Loader className="w-8 h-8 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-600">Analyzing Excel file...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-red-500 mb-4">{error}</div>
        <PrimaryButton onClick={onCancel}>Go Back</PrimaryButton>
      </div>
    );
  }

  if (!fileInfo) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-gray-500 mb-4">No file selected</div>
        <PrimaryButton onClick={onCancel}>Go Back</PrimaryButton>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Configure Excel Sheets
      </h3>

      <div className="text-sm text-gray-600 mb-4">
        <p>File: {fileInfo.fileName}</p>
        <p>Size: {Math.round(fileInfo.fileSize / 1024)} KB</p>
        <p>Sheets: {fileInfo.sheetNames.length}</p>
      </div>

      <div className="border-b border-gray-200 mb-4">
        <div className="flex overflow-x-auto">
          {fileInfo.sheetNames.map((name) => (
            <button
              key={name}
              className={`py-2 px-4 text-sm font-medium ${
                activeSheet === name
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveSheet(name)}
            >
              <div className="flex items-center">
                {sheetConfigs[name]?.isValid ? (
                  <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 mr-1 text-red-500" />
                )}
                {name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeSheet && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`include-${activeSheet}`}
                checked={sheetConfigs[activeSheet]?.include || false}
                onChange={() => handleIncludeToggle(activeSheet)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              <label
                htmlFor={`include-${activeSheet}`}
                className="text-sm font-medium text-gray-700"
              >
                Include this sheet
              </label>
            </div>

            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-700 mr-2">
                Scenario Name:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={sheetConfigs[activeSheet]?.newName || ""}
                  onChange={(e) =>
                    handleNameChange(activeSheet, e.target.value)
                  }
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  disabled={!sheetConfigs[activeSheet]?.include}
                />
                <Edit2 className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          {!sheetConfigs[activeSheet]?.isValid && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-yellow-700 font-medium">
                    This sheet is missing required columns:
                  </p>
                  <ul className="mt-1 text-sm text-yellow-700 list-disc list-inside">
                    {fileInfo.sheetsPreview[activeSheet].missingColumns.map(
                      (col) => (
                        <li key={col}>{col}</li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {fileInfo.sheetsPreview[activeSheet].headers.map(
                    (header, index) => (
                      <th
                        key={index}
                        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fileInfo.sheetsPreview[activeSheet].preview.map(
                  (row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-3 py-2 whitespace-nowrap text-xs text-gray-500"
                        >
                          {cell !== undefined && cell !== null
                            ? String(cell)
                            : ""}
                        </td>
                      ))}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 mt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <PrimaryButton
          onClick={handleUpload}
          isLoading={isSubmitting}
          loadingText="Uploading..."
          disabled={
            isSubmitting ||
            !Object.values(sheetConfigs).some((config) => config.include)
          }
        >
          Upload Scenarios
        </PrimaryButton>
      </div>
    </div>
  );
};

export default ExcelPreview;
